import { useEffect, useMemo, useRef, useState } from 'react';
import type { LngLatBoundsLike } from 'mapbox-gl';
import { NJFormItem, NJIconButton, NJInlineMessage, NJText, NJTooltip } from '@engie-group/fluid-design-system-react';
import { UMButton, UMFormGroup, UMInput } from '../../urbanmap/_components';
import { PlanSegmentedControl } from './PlanSegmentedControl';
import { ProgressLabel, ProgressMark, type ProgressItem, type SectionKey, type SectionProgress } from './CreationProgress';
import { CommuneSearch } from './CommuneSearch';
import { ContractFields } from './ContractFields';
import { centerOf, inPerimeter } from './domain';
import { NetworkPicker } from './NetworkPicker';
import { communesFC } from './mapLayers';
import { networkInPerimeter } from './networkScope';
import { polygonsOverlap, validLasso } from './selection/lasso';
import { PerimeterMap } from './PerimeterMap';
import { PlaceSearch } from './PlaceSearch';
import { PlanFormStyles } from './PlanFormStyles';
import { StationCard } from './StationCard';
import { StationSection } from './StationSection';
import { PC, caption, card } from './tokens';
import type { ContractPeriod, DrawShape, LngLat, Perimeter, PlanBuilding, ProjectKind, Referential } from './types';

export type PerimeterMode = 'communes' | 'drawn';

export interface PerimeterDraft {
  name: string;
  description: string;
  kind: ProjectKind;
  networkIds: string[];
  contract: ContractPeriod;
  mode: PerimeterMode;
  communes: string[];
  shape: DrawShape;
  points: LngLat[];
  closed: boolean;
  drawing: boolean;
}

interface Props {
  referential: Referential;
  draft: PerimeterDraft;
  onChange: (patch: Partial<PerimeterDraft>) => void;
  included: PlanBuilding[];
  includedIds: Set<string>;
  /** Station météo retenue (section 3) et sélection. */
  stationId: string;
  onStationSelect: (id: string) => void;
  progress: SectionProgress;
  onTouch: (key: SectionKey) => void;
  actions: React.ReactNode;
  /** Configuration d'un projet existant : périmètre affiché en lecture seule avec ce message. */
  lockedPerimeter?: string;
  /** Cadrage initial de la carte, utilisé à la création du projet. */
  initialBounds?: LngLatBoundsLike;
}

const TABS: Record<PerimeterMode, string> = { communes: 'Communes', drawn: 'Zone sur la carte' };

function Section({ title, icon, item, onTouch, children, required = false }: { title: string; icon: string; item: ProgressItem; onTouch: () => void; children: React.ReactNode; required?: boolean }) {
  const [focused, setFocused] = useState(false);
  const display = focused && item.state === 'untouched' ? { ...item, state: 'current' as const } : item;
  return (
    <li
      className="pcv3-creation-section"
      aria-label={`${title}${required ? ' (obligatoire)' : ''}`}
      aria-current={focused ? 'step' : undefined}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) { setFocused(false); onTouch(); }
      }}
    >
      <div className="pcv3-creation-section-heading" data-state={display.state}>
        <ProgressMark item={display} icon={icon} />
        <div className="pcv3-progress-copy" aria-live="polite">
          <NJText as="strong" scale="sm">{title}{required && <> <span aria-hidden="true">*</span></>}</NJText>
          <ProgressLabel item={display} />
        </div>
      </div>
      {children}
    </li>
  );
}

/** Étape 1 plein écran : identité du projet, réseaux repris, contrat et périmètre géographique. */
export function StepPerimeter({ referential, draft, onChange: updateDraft, included, includedIds, stationId, onStationSelect, progress, onTouch, actions, lockedPerimeter, initialBounds }: Props) {
  const [focus, setFocus] = useState<{ id: string; shape: LngLat[]; code: string } | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const [stationToReveal, setStationToReveal] = useState<{ position: LngLat; serviceAreas?: (GeoJSON.Polygon | GeoJSON.MultiPolygon)[] } | null>(null);
  const [visible, setVisible] = useState({
    included: true, excluded: true, communes: true, engie: true, otherNetworks: true, stations: true, weatherZone: true,
  });
  const toggleLayer = (key: keyof typeof visible) => setVisible((current) => ({ ...current, [key]: !current[key] }));
  const previousZone = useRef<Pick<PerimeterDraft, 'points' | 'closed' | 'networkIds'> | null>(null);
  const goTo = (insee: string) => {
    const shape = referential.communeOutlines[insee];
    if (shape) setFocus({ id: `${insee}-${Date.now()}`, shape, code: insee });
  };
  const outlines = useMemo(() => draft.mode === 'communes'
    ? draft.communes.map((c) => referential.communeOutlines[c]).filter((r): r is LngLat[] => !!r)
    : [], [draft.mode, draft.communes, referential.communeOutlines]);
  const perimeter = useMemo<Perimeter>(() => draft.mode === 'communes'
    ? { kind: 'communes', communes: draft.communes }
    : { kind: 'drawn', polygon: draft.closed ? draft.points : [] },
  [draft.mode, draft.communes, draft.closed, draft.points]);
  const networks = useMemo(() => referential.networks.filter((network) =>
    networkInPerimeter(network, perimeter) || (focus !== null && network.communes.includes(focus.code))),
  [referential.networks, perimeter, focus]);
  const networkOptions = useMemo(() => draft.mode === 'communes'
    ? referential.networks.filter((n) => networkInPerimeter(n, perimeter))
    : networks, [draft.mode, referential.networks, perimeter, networks]);
  const outlineData = useMemo(() => communesFC(
    draft.mode === 'communes' ? draft.communes : [],
    referential.communeGeometries,
  ), [draft.mode, draft.communes, referential.communeGeometries]);
  const onChange = (patch: Partial<PerimeterDraft>) => {
    const next = { ...draft, ...patch };
    const scope: Perimeter = next.mode === 'communes'
      ? { kind: 'communes', communes: next.communes }
      : { kind: 'drawn', polygon: next.closed ? next.points : [] };
    const changesScope = patch.communes !== undefined || patch.mode !== undefined
      || patch.points !== undefined || patch.closed !== undefined;
    updateDraft(changesScope && !lockedPerimeter ? {
      ...patch,
      networkIds: next.networkIds.filter((id) => [...networks, ...referential.networks].some((n) =>
        n.id === id && networkInPerimeter(n, scope))),
    } : patch);
  };
  const startLasso = () => {
    setLassoError(null);
    previousZone.current ??= { points: draft.points, closed: draft.closed, networkIds: draft.networkIds };
    onChange({ points: [], closed: false, drawing: true, shape: 'free' });
  };
  const [lassoError, setLassoError] = useState<string | null>(null);
  const finishLasso = (points: LngLat[]) => {
    if (!validLasso(points)) {
      setLassoError('Ajoutez au moins trois sommets formant une zone avec une surface suffisante.');
      return;
    }
    setLassoError(null);
    onChange({ points, drawing: false, closed: false });
  };
  const validateLasso = () => {
    previousZone.current = null;
    onChange({ closed: true, drawing: false });
  };
  const beginLassoEdit = () => {
    previousZone.current ??= { points: draft.points, closed: draft.closed, networkIds: draft.networkIds };
    onChange({ closed: false });
  };
  const editLasso = (points: LngLat[]) => {
    setLassoError(validLasso(points) ? null : 'La zone doit conserver au moins trois sommets et une surface suffisante.');
    onChange({ points, closed: false, drawing: false });
  };
  const cancelLasso = () => {
    setLassoError(null);
    onChange({ ...previousZone.current, drawing: false });
    previousZone.current = null;
  };
  useEffect(() => {
    if (draft.mode !== 'drawn' || draft.closed || (!draft.drawing && !draft.points.length)) return;
    const shortcut = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLElement && event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelLasso();
      } else if (event.key === 'Enter' && !event.repeat) {
        if (event.target instanceof HTMLElement && event.target.closest('button')) return;
        event.preventDefault();
        if (draft.drawing) finishLasso(draft.points);
        else if (validLasso(draft.points)) validateLasso();
      }
    };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, [draft.mode, draft.drawing, draft.closed, draft.points, updateDraft]);
  const perimeterReady = draft.mode === 'communes'
    ? draft.communes.length > 0
    : draft.closed && !draft.drawing && validLasso(draft.points);
  useEffect(() => {
    if (!perimeterReady) {
      setOpened(null);
      setStationToReveal(null);
    }
  }, [perimeterReady]);
  const candidateReady = draft.mode === 'drawn' && !draft.drawing && validLasso(draft.points);
  const mapIncludedIds = useMemo(() => candidateReady
    ? new Set(referential.buildings.filter((building) => inPerimeter(building, { kind: 'drawn', polygon: draft.points })).map((building) => building.id))
    : includedIds, [candidateReady, referential.buildings, draft.points, includedIds]);
  const empty = perimeterReady && included.length === 0;
  const weatherCommunes = useMemo(() => new Set(draft.mode === 'communes'
    ? draft.communes
    : Object.entries(referential.communeOutlines)
      .filter(([, outline]) => draft.closed && polygonsOverlap(outline, draft.points))
      .map(([code]) => code)),
  [draft.mode, draft.communes, draft.closed, draft.points, referential.communeOutlines]);
  /** Les stations météo n'apparaissent qu'une fois le périmètre renseigné. */
  const stations = useMemo(() => perimeterReady
    ? referential.stations.map((s) => ({ id: s.id, name: s.name, position: s.position, area: [],
      hidePoint: !visible.stations,
      serviceAreas: visible.weatherZone && (s.id === stationId || s.communes.some((code) => weatherCommunes.has(code))) ? s.serviceAreas : [] }))
    : [], [perimeterReady, referential.stations, stationId, weatherCommunes, visible.stations, visible.weatherZone]);
  const mapBuildings = useMemo(() => referential.buildings.filter((building) =>
    mapIncludedIds.has(building.id) ? visible.included : visible.excluded),
  [referential.buildings, mapIncludedIds, visible.included, visible.excluded]);
  const mapNetworks = useMemo(() => referential.networks.filter((network) =>
    network.isEngie === true ? visible.engie : visible.otherNetworks),
  [referential.networks, visible.engie, visible.otherNetworks]);
  const mapOutlineData = useMemo(() => visible.communes ? outlineData : communesFC([], {}),
    [visible.communes, outlineData]);
  const center = useMemo(() => centerOf(included.map((b) => b.centroid)), [included]);
  const openedStation = perimeterReady ? referential.stations.find((s) => s.id === opened) ?? null : null;

  return (
    <div className="pcv3-perimeter-layout" style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <PlanFormStyles />
      <aside className="pcv3-creation-sidebar" aria-label="Formulaire du projet">
      <ol className="plan-panel pcv3-creation-panel" aria-label="Rubriques du formulaire">
        <li><NJText scale="xs">* Champs obligatoires</NJText></li>
        <Section title="Nom du projet" icon="drive_file_rename_outline" item={progress.name} onTouch={() => onTouch('name')} required>
          <UMFormGroup label="Nom du projet">
            <UMInput value={draft.name} onChange={(name) => onChange({ name })} placeholder="Ex. Ivry — extension réseau 2028" ariaLabel="Nom du projet (obligatoire)" />
          </UMFormGroup>
          <div className="pcv3-project-description">
          <NJFormItem
            id="project-description"
            label="Description (facultative)"
            labelKind="static"
            size="sm"
            isMultiline
            rows={2}
            value={draft.description}
            placeholder="Décrivez brièvement le projet"
            ariaLabel="Description du projet (facultative)"
            onChange={(event) => onChange({ description: (event.target as HTMLTextAreaElement).value })}
          />
          </div>
        </Section>

        <Section title="Périmètre géographique" icon="map" item={progress.perimeter} onTouch={() => onTouch('perimeter')} required>
          <fieldset aria-label="Périmètre géographique (obligatoire)" aria-required="true" disabled={!!lockedPerimeter} style={{ border: 0, margin: 0, padding: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {lockedPerimeter && <div style={caption}>{lockedPerimeter}</div>}
          <PlanSegmentedControl
            label="Mode de sélection du périmètre"
            options={Object.values(TABS)}
            value={TABS[draft.mode]}
            onChange={(v) => onChange({ mode: v === TABS.drawn ? 'drawn' : 'communes', drawing: false, shape: 'free' })}
          />

          {draft.mode === 'communes' ? (
            <CommuneSearch
              communes={referential.communes}
              selected={draft.communes}
              onChange={(communes) => onChange({ communes })}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <PlaceSearch communes={referential.communes} onPick={goTo} />
              <NJText scale="xs" role="status">
                {draft.drawing
                  ? 'Cliquez pour ajouter les sommets. Double-cliquez ou appuyez sur Entrée pour terminer le tracé. Échap annule.'
                  : draft.points.length > 0 && !draft.closed
                    ? 'Déplacez la zone, ses sommets ou les points intermédiaires pour la modifier. Valider ou Entrée confirme ; Échap annule.'
                    : draft.closed
                      ? 'Zone validée : seuls les bâtiments à l’intérieur sont copiés.'
                      : 'Recherchez le lieu à étudier, puis sélectionnez une zone au lasso.'}
              </NJText>
              {lassoError && <NJInlineMessage variant="error" scale="sm" role="alert">{lassoError}</NJInlineMessage>}
              <div style={{ display: 'flex', gap: 'var(--nj-semantic-size-spacing-4)', flexWrap: 'nowrap', alignItems: 'center' }}>
                {!draft.drawing && (draft.closed || !draft.points.length) && (
                <NJTooltip placement="top" text={<NJText scale="xs">{draft.closed ? 'Déplacez les sommets ou la zone. Entrée : valider. Échap : annuler.' : 'Clic : ajouter un sommet. Double-clic ou Entrée : terminer.'}</NJText>}>
                <span>
                <UMButton
                  icon="polyline"
                  disabled={draft.drawing}
                  onClick={draft.closed ? beginLassoEdit : startLasso}
                >
                  {draft.points.length > 0 ? 'Modifier' : 'Lasso'}
                </UMButton>
                </span>
                </NJTooltip>
                )}
                {(draft.drawing || (draft.points.length > 0 && !draft.closed)) && (
                  <>
                  {!draft.drawing && (
                    <NJTooltip placement="top" text={<NJText scale="xs">Entrée : confirmer la sélection.</NJText>}>
                      <span><UMButton icon="check" disabled={!validLasso(draft.points)} onClick={validateLasso}>Valider</UMButton></span>
                    </NJTooltip>
                  )}
                  <NJTooltip placement="top" text={<NJText scale="xs">{draft.drawing ? 'Double-clic ou Entrée : terminer. Échap : annuler.' : 'Échap : annuler et restaurer la zone précédente.'}</NJText>}>
                    <span><UMButton variant="secondary" icon="close" onClick={cancelLasso}>Annuler</UMButton></span>
                  </NJTooltip>
                  </>
                )}
                {draft.closed && (
                  <NJTooltip placement="top" text={<NJText scale="xs">Retirer la zone pour recommencer.</NJText>}>
                    <span><UMButton variant="secondary" icon="delete"
                      onClick={() => { setLassoError(null); previousZone.current = null; onChange({ points: [], closed: false, drawing: false }); }}>Effacer</UMButton></span>
                  </NJTooltip>
                )}
              </div>
            </div>
          )}
          </fieldset>
        </Section>

        {empty && (
          <li>
            <NJInlineMessage variant="error" scale="sm" role="status" style={{ alignItems: 'center' }}>
              Aucun bâtiment UrbanMap dans ce périmètre : modifiez-le.
            </NJInlineMessage>
          </li>
        )}

        <Section title="Station météo" icon="thermostat" item={progress.weather} onTouch={() => onTouch('weather')} required>
          <StationSection
            stations={referential.stations}
            selected={stationId}
            center={center}
            ready={perimeterReady}
            onSelect={(id) => {
              onStationSelect(id);
              setOpened(id);
              setVisible((current) => ({ ...current, stations: true, weatherZone: true }));
              const station = referential.stations.find((s) => s.id === id);
              if (station) setStationToReveal({ position: station.position, serviceAreas: station.serviceAreas });
            }}
          />
        </Section>

        <Section title="Type de projet" icon="hub" item={progress.kind} onTouch={() => onTouch('kind')} required>
          <NetworkPicker
            kind={draft.kind}
            onKindChange={(kind) => onChange({ kind, networkIds: kind === 'new' ? [] : draft.networkIds })}
            networks={networkOptions}
            selected={draft.networkIds}
            onSelectedChange={(networkIds) => onChange({ networkIds })}
            ready={perimeterReady}
          />
        </Section>

        <Section title="Période du contrat" icon="date_range" item={progress.contract} onTouch={() => onTouch('contract')} required>
          <ContractFields value={draft.contract} onChange={(contract) => onChange({ contract })} />
        </Section>
      </ol>
      {actions}
      </aside>

      <div className="pcv3-perimeter-map" style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <PerimeterMap
          buildings={mapBuildings}
          included={mapIncludedIds}
          outlines={outlines}
          outlineData={mapOutlineData}
          networks={mapNetworks}
          selectedNetworkIds={draft.networkIds}
          onNetworkPick={!lockedPerimeter && draft.kind === 'extension' && perimeterReady
            ? (id) => onChange({
              networkIds: draft.networkIds.includes(id)
                ? draft.networkIds.filter((networkId) => networkId !== id)
                : [...draft.networkIds, id],
            })
            : undefined}
          focus={focus}
          initialBounds={initialBounds}
          drawing={draft.mode === 'drawn' && draft.drawing}
          shape={draft.shape}
          points={draft.mode === 'drawn' && !draft.closed ? draft.points : []}
          closed={draft.mode === 'drawn' && !draft.drawing && draft.points.length >= 3}
          editable={draft.mode === 'drawn' && !draft.closed && !lockedPerimeter}
          onEditStart={beginLassoEdit}
          onEdit={editLasso}
          onPolygon={finishLasso}
          onDrawPoint={(points) => { setLassoError(null); onChange({ points, drawing: true, closed: false }); }}
          onCancelDrawing={cancelLasso}
          stations={stations}
          selectedStation={stationId}
          stationToReveal={perimeterReady ? stationToReveal : null}
          onStationPick={perimeterReady && (visible.stations || visible.weatherZone) ? setOpened : undefined}
          anchor={openedStation ? openedStation.position : null}
        >
          {(pos) => openedStation && (visible.stations || visible.weatherZone) && (
            <StationCard
              station={openedStation}
              selected={openedStation.id === stationId}
              at={pos}
              onChoose={() => onStationSelect(openedStation.id)}
              onClose={() => setOpened(null)}
            />
          )}
        </PerimeterMap>
        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 5, ...card, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          <Legend color={PC.included} label="Inclus dans le projet" visible={visible.included} onToggle={() => toggleLayer('included')} />
          <Legend color={PC.excluded} label="Bâtiment UrbanMap hors périmètre" dashed visible={visible.excluded} onToggle={() => toggleLayer('excluded')} />
          <Legend color={PC.outline} label="Contour des communes" dashed line visible={visible.communes} onToggle={() => toggleLayer('communes')} />
          {referential.networks.some((n) => n.isEngie === true) && <Legend color={PC.networkEngie} label="Réseau ENGIE" line visible={visible.engie} onToggle={() => toggleLayer('engie')} />}
          {referential.networks.some((n) => n.isEngie !== true) && <Legend color={PC.networkNonEngie} label="Réseau non-ENGIE" line visible={visible.otherNetworks} onToggle={() => toggleLayer('otherNetworks')} />}
          {referential.networks.length > 0 && <NJText scale="sm">Trait épais : réseau retenu</NJText>}
          {stations.length > 0 && <>
            <Legend color={PC.station} label="Stations météo" circle visible={visible.stations} onToggle={() => toggleLayer('stations')} />
            <Legend color={PC.station} label="Zones de référence météo" dotted line visible={visible.weatherZone} onToggle={() => toggleLayer('weatherZone')} />
          </>}
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label, dashed, dotted, line, circle, visible, onToggle }: { color: string; label: string; dashed?: boolean; dotted?: boolean; line?: boolean; circle?: boolean; visible: boolean; onToggle: () => void }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--nj-semantic-size-spacing-8)' }}>
      <NJIconButton icon={visible ? 'visibility' : 'visibility_off'} label={`${visible ? 'Masquer' : 'Afficher'} — ${label}`}
        ariaPressed={visible} variant="tertiary" scale="2xs" onClick={onToggle} />
      <span
        aria-hidden="true"
        style={line
          ? { width: 24, height: 0, flexShrink: 0, borderTop: `3px ${dotted ? 'dotted' : dashed ? 'dashed' : 'solid'} ${color}`, display: 'inline-block' }
          : { width: circle ? 12 : 14, height: circle ? 12 : 10, flexShrink: 0, borderRadius: circle ? '50%' : 0, border: `2px ${dashed ? 'dashed' : 'solid'} ${color}`, background: dashed ? 'transparent' : color }}
      />
      {label}
    </span>
  );
}
